import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  listTreatmentPlans,
  deleteTreatmentPlan,
} from "../../../../actions/treatmentPlanActions.js";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/shared/Table/index.js";
import { Box, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { columnsDesktop } from "../columns/TreatmentPlanListColumns.js"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/TreatmentPlanListColumnsMobile.js"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import {
  TREATMENTPLAN_CREATE_RESET,
  TREATMENTPLAN_DELETE_RESET,
  TREATMENTPLAN_UPDATE_RESET,
} from "../../../../constants/treatmentPlanConstants.js";
import SidePanel from "../../../../components/sidepanelNew/SidePanel.js";
import useRoleCheck from "../../../../hooks/useRoleCheck.js";
import useThemeColors from "../../../../hooks/useThemeColors.js";
import { white, primary, textSecondary } from "../../../../utils/colors.js";
import TreatmentPlansStatsNew from "../partials/TreatmentPlansStatsNew.js";
import SearchInput from "../../../../components/input/SearchInput.js";
import CreateTreatmentPlanNew from "../sidepanels/CreateTreatmentPlanNew.js";
import EditTreatmentPlanNew from "../sidepanels/EditTreatmentPlanNew.js";
import useDebounce from "../../../../hooks/useDebounce.js";
import PageHeader from "../../../../components/layout/PageHeader.js";
import ActiveFilter from "../partials/ActiveFilter.js";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed.js";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal.js";
import TreatmentPlansFilterPopover from "../partials/TreatmentPlansFilterPopover.js";
import TreatmentPlansStatsNewMobile from "../partials/TreatmentPlansStatsNewMobile.js";
import useIsMobile from "../../../../hooks/useIsMobile.js";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol.js";
import UserNoAccess from "../../../../components/partials/UserNoAccess.js";
import DrawerNew from "../../../../components/menu/DrawerNew.js";
import CardList from "../../../../components/mobile/CardList.js";
import TreatmentPlanCardMobile from "../partials/TreatmentPlanCardMobile.js";

const TreatmentPlanListNew = () => {
  // GENERAL
  const { slug } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { whiteMode } = useThemeColors();
  const translate = (key) => t(key);
  const isMobile = useIsMobile();

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // STATE
  const [planId, setPlanId] = useState("");
  const [treatmentPlanID, setTreatmentPlanID] = useState("");
  const [patientName, setPatientName] = useState("");

  // ROLES
  const isAdmin = useRoleCheck("admin");

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
  };

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 30];

  // SIDEPANELS  AND MODALS STATE
  const [createSidePanelOpen, setCreateSidePanelOpen] = useState(false);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // State to control delete confirmation modal

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const treatmentPlanList = useSelector((state) => state.treatmentPlanList);
  const {
    loading,
    error,
    treatmentPlans,
    pages,
    countsByStatus,
    globalTotalCount,
    averagePrice,
    highestServicePrice,
    countsByServiceTotalPrice,
    totalCountBasedOnFilter,
  } = treatmentPlanList;

  const treatmentPlanCreate = useSelector((state) => state.treatmentPlanCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    treatmentPlan: createdTreatmentPlan,
  } = treatmentPlanCreate;

  const treatmentPlanUpdate = useSelector((state) => state.treatmentPlanUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = treatmentPlanUpdate;

  const treatmentPlanDelete = useSelector((state) => state.treatmentPlanDelete);

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = treatmentPlanDelete;

  // SORTING
  const [sortField, setSortField] = useState("treatmentPlanID");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = (column) => {
    const newSortOrder =
      sortField === column && sortOrder === "asc" ? "desc" : "asc";

    if (sortField !== column || sortOrder !== newSortOrder) {
      // Only dispatch the action if sorting parameters change
      console.log(`Sorting ${column} in ${newSortOrder} order`);

      setSortField(column);
      setSortOrder(newSortOrder);
    }
  };

  // FILTERING
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFilter, setResetFilter] = useState(false);
  const [filterParams, setFilterParams] = useState({
    employeeId: [],
    status: [],
    startDate: "",
    endDate: "",
    minPrice: "",
    maxPrice: "",
    creatorEmployeeId: [],
    coordinatorId: [],
  });
  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false);

    localStorage.setItem(
      "filterSettings_treatmentPlans",
      JSON.stringify(filters)
    ); // Example for PatientList
    dispatch(
      listTreatmentPlans(
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false,
        filters.employeeId,
        filters.status,
        filters.startDate,
        filters.endDate,
        filters.minPrice,
        filters.maxPrice,
        filters.creatorEmployeeId,
        filters.coordinatorId,
        slug
      )
    );

    console.log("filters", filters);
  };

  // RESET FILTER
  const handleResetFilter = () => {
    const resetState = {
      employeeId: [],
      status: [],
      startDate: "",
      endDate: "",
      minPrice: "",
      maxPrice: "",
      creatorEmployeeId: [],
      coordinatorId: [],
    };
    setFilterParams(resetState);
    localStorage.setItem(
      "filterSettings_treatmentPlans",
      JSON.stringify(resetState)
    ); // Reset localStorage

    console.log("Resetting filters", resetState);
    console.log(
      "Resetting filters",
      localStorage.getItem("filterSettings_treatmentPlans")
    );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      listTreatmentPlans(
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false,
        resetState.employeeId,
        resetState.status,
        resetState.startDate,
        resetState.endDate,
        resetState.minPrice,
        resetState.maxPrice,
        resetState.creatorEmployeeId,
        resetState.coordinatorId,
        slug
      )
    );
  };

  // useEffect(() => {
  //   const savedFiltersStr = localStorage.getItem(
  //     "filterSettings_treatmentPlans"
  //   );
  //   if (savedFiltersStr) {
  //     const savedFilters = JSON.parse(savedFiltersStr);
  //     setFilterParams(savedFilters);
  //     setIsFilterActive(true);
  //     console.log("Found saved filters and applied", savedFilters);
  //   } else {
  //     console.log("No saved filters found");
  //   }
  //   setFiltersInitialized(true); // Indicate that filter initialization is complete
  // }, []);

  // DELETE HANDLER
  const deleteTreatmentPlanHandler = (planId) => {
    dispatch(deleteTreatmentPlan(slug, planId));
  };

  // SIDEPANEL HANDLERS
  // CREATE SIDE PANEL
  const openCreateSidePanel = () => {
    setCreateSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeCreateSidePanel = () => {
    setCreateSidePanelOpen(false);
    document.body.classList.remove("no-scroll");
  };

  // EDIT SIDE PANEL
  const openEditSidePanel = () => {
    setEditSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeEditSidePanel = () => {
    setPlanId(null);
    setEditSidePanelOpen(false);
    document.body.classList.remove("no-scroll");
  };

  // FETCH DATA

  // USE EFFECT

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listTreatmentPlans(
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          false,
          filterParams.employeeId,
          filterParams.status,
          filterParams.startDate,
          filterParams.endDate,
          filterParams.minPrice,
          filterParams.maxPrice,
          filterParams.creatorEmployeeId,
          filterParams.coordinatorId,
          slug
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    successUpdate,
    slug,

    // successApprove,
    // successCancel,
  ]);

  // TOASTS
  // CREATE TOASTS
  useEffect(() => {
    if (successCreate) {
      toast({
        title: t("toasts.treatmentPlanCreate"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      closeCreateSidePanel();

      dispatch({ type: TREATMENTPLAN_CREATE_RESET });
    } else if (errorCreate) {
      if (
        errorCreate ===
        "Monthly limit reached. Cannot create more treatment plans."
      ) {
        toast({
          title: t("toasts.treatmentPlanLimitReached"),
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("toasts.treatmentPlanCreateError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }

      dispatch({ type: TREATMENTPLAN_CREATE_RESET });
    }
  }, [successCreate, errorCreate]);

  // EDIT TOASTS
  useEffect(() => {
    if (successUpdate) {
      toast({
        title: t("toasts.treatmentPlanUpdate"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      closeEditSidePanel();

      dispatch({ type: TREATMENTPLAN_UPDATE_RESET });
    } else if (errorUpdate) {
      toast({
        title: t("toasts.treatmentPlanUpdateError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [successUpdate, errorUpdate]);

  // DELETE TOASTS
  useEffect(() => {
    if (successDelete) {
      toast({
        title: t("toasts.treatmentPlanDelete"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowDeleteConfirmation(false);
      setTreatmentPlanID(null);
      setPatientName(null);
    } else if (errorDelete) {
      toast({
        title: t("toasts.treatmentPlanDeleteError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    dispatch({ type: TREATMENTPLAN_DELETE_RESET });
  }, [successDelete, errorDelete]);

  const currencySymbol = useCurrencySymbol(tenant && tenant.currency);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.treatmentPlans?.on;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.treatmentPlans?.delete;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.treatmentPlans?.create;
  const hasAccessToUpdate =
    currentTenantAccess?.permissions?.treatmentPlans?.update;
  // const hasAccessToDuplicate =
  //   currentTenantAccess?.permissions?.treatmentPlans?.duplicate;
  const hasAccessToPatientSensitiveData =
    currentTenantAccess?.permissions?.patients?.sensitiveData;

  const hasAccessToSendToPatient =
    currentTenantAccess?.permissions?.treatmentPlans?.sendToPatient;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.treatmentPlans?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug, navigate]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.treatmentPlans")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching;

  // LOGS
  // console.log("treatmentPlans", treatmentPlans);

  // console.log("currency", tenant && tenant.currency);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.treatmentPlans")}</title>
        <meta
          name="description"
          content={t("pageDescription.treatmentPlans")}
        />
      </Helmet>

      {/* SIDEPANELS START  */}
      {!isMobile ? (
        <SidePanel
          open={createSidePanelOpen}
          close={closeCreateSidePanel}
          title={t("treatmentPlan.createTreatmentPlan")}
          noBorder={true}
        >
          <Flex w="100%" bg={white} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateTreatmentPlanNew
                hasAccessToPatientSensitiveData={
                  hasAccessToPatientSensitiveData
                }
                tenant={tenant}
                close={closeCreateSidePanel}
                currentPatient={null}
                currentUser={userInfo}
                isPatientPage={false}
                currency={tenant && tenant.currency}
              />
            ) : (
              <UserNoAccess
                pageName={t("noAccess.treatmentPlanCreate")}
                p="0"
              />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={createSidePanelOpen}
          onClose={closeCreateSidePanel}
          title={t("treatmentPlan.createTreatmentPlan")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateTreatmentPlanNew
              hasAccessToPatientSensitiveData={hasAccessToPatientSensitiveData}
              tenant={tenant}
              close={closeCreateSidePanel}
              currentPatient={null}
              currentUser={userInfo}
              isPatientPage={false}
              currency={tenant && tenant.currency}
            />
          ) : (
            <UserNoAccess pageName={t("noAccess.treatmentPlanCreate")} p="0" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editSidePanelOpen}
          close={closeEditSidePanel}
          title={t("treatmentPlan.editTreatmentPlan")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditTreatmentPlanNew
              hasEditAccess={hasAccessToUpdate}
              hasAccessToPatientSensitiveData={hasAccessToPatientSensitiveData}
              planId={planId}
              close={closeEditSidePanel}
              currentUser={userInfo}
              currency={tenant && tenant.currency}
              hasAccessSendToPatient={hasAccessToSendToPatient}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editSidePanelOpen}
          onClose={closeEditSidePanel}
          title={t("treatmentPlan.editTreatmentPlan")}
          size="full"
        >
          <EditTreatmentPlanNew
            hasEditAccess={hasAccessToUpdate}
            hasAccessToPatientSensitiveData={hasAccessToPatientSensitiveData}
            planId={planId}
            close={closeEditSidePanel}
            currentUser={userInfo}
            currency={tenant && tenant.currency}
            hasAccessSendToPatient={hasAccessToSendToPatient}
          />
        </DrawerNew>
      )}

      {/* SIDEPANELS END  */}

      {/* MODALS  */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.treatmentPlanDelete")}
          deleteTitle={t("treatmentPlan.deleteTreatmentPlanTitle")}
          deleteName={treatmentPlanID + " - " + patientName}
          // warningLabel={t("treatmentPlan.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deleteTreatmentPlanHandler(planId); // Use `userId` set from `MoreButtonLite`
          }}
        />
      )}

      {/* MAIN  */}
      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 0rem", "0rem 0rem 0rem", "2rem 2rem 6rem"]}
        >
          <Box
            w="100%"
            h="100%"
            bg={whiteMode}
            overflow="hidden"
            borderRadius={["0rem", "2rem"]}
          >
            <PageHeader
              label={t("treatmentPlan.treatmentPlans")}
              buttonHandler={openCreateSidePanel}
              buttonLabel={t("treatmentPlan.createTreatmentPlan")}
              filter={
                // <FilterPopover
                //     isOpen={isOpenFilter}
                //     onOpen={onOpenFilter}
                //     onClose={onCloseFilter}
                //     onFilterApply={handleFilterApply} // Already defined in your code
                //     isFilterActive={isFilterActive} // Already defined in your code
                //     setIsFilterActive={setIsFilterActive} // Already defined in your code
                //     filterConfig={filterConfig} // Already defined in your code
                //     filterStatusOptions="user"
                //     savedFilters={filterParams}
                //     savedFiltersKey="filterSettings_treatmentPlans"
                //   />

                <TreatmentPlansFilterPopover
                  onFilterApply={handleFilterApply}
                  isFilterActive={isFilterActive}
                  setIsFilterActive={setIsFilterActive}
                  filterStatusOptions="treatment"
                  savedFiltersKey="filterSettings_treatmentPlans"
                  resetFilter={resetFilter}
                  onResetComplete={() => setResetFilter(false)}
                />
              }
            />

            {!isMobile ? (
              <TreatmentPlansStatsNew
                loading={loading}
                countsByStatus={countsByStatus}
                globalTotalCount={globalTotalCount}
                isFilterActive={isFilterActive}
                averagePrice={averagePrice}
                highestServicePrice={highestServicePrice}
                countsByServiceTotalPrice={countsByServiceTotalPrice}
                currencySymbol={currencySymbol}
              />
            ) : (
              <TreatmentPlansStatsNewMobile
                loading={loading}
                countsByStatus={countsByStatus}
                globalTotalCount={globalTotalCount}
                isFilterActive={isFilterActive}
                averagePrice={averagePrice}
                highestServicePrice={highestServicePrice}
                countsByServiceTotalPrice={countsByServiceTotalPrice}
              />
            )}

            <SearchInput
              placeholder={t("treatmentPlan.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0 1.5rem", "0 2rem"]}
            />

            {isFilterActive && (
              <ActiveFilter
                filterParams={filterParams}
                setIsFilterActive={setIsFilterActive}
                onFilterApply={handleFilterApply}
                onReset={handleResetFilter}
              />
            )}

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={treatmentPlans}
                      columns={
                        isMobile
                          ? columnsMobile(translate, handleSortChange)
                          : columnsDesktop(
                              translate,
                              navigate,
                              deleteTreatmentPlanHandler,
                              handleSortChange,
                              slug,
                              setShowDeleteConfirmation,
                              setPlanId,
                              openEditSidePanel,
                              setTreatmentPlanID,
                              setPatientName,
                              currencySymbol
                            )
                      }
                      noItemsTitle={t("noItems.noTreatmentPlans")}
                      noItemsDescription={t("noItems.noTreatmentPlansText")}
                      noItemsButtonLabel={t("noItems.noTreatmentPlansButton")}
                      noItemsButtonHandler={openCreateSidePanel}
                      onRowClick={(planId) => {
                        // console.log("onrowclick planId", planId);
                        setPlanId(planId);
                        openEditSidePanel();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />

                    <Table.Pagination
                      loading={loading}
                      page={page}
                      pages={pages}
                      pageSize={pageSize}
                      pageSizes={pageSizes}
                      navigate={navigate}
                      setPage={setPage}
                      setPageSize={setPageSize}
                      totalCount={totalCountBasedOnFilter}
                    />
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p={["3rem 1.5rem", "3rem 2rem"]}
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <Flex
                w="100%"
                justify="center"
                align="flex-start"
                p="1rem 0rem"
                minH="800px"
              >
                {!isLoading ? (
                  <Box p="0" w="100%">
                    {treatmentPlans && treatmentPlans?.length > 0 ? (
                      <>
                        <CardList
                          p="1.25rem 0rem"
                          slug={slug}
                          data={treatmentPlans}
                          loading={loading}
                          sortField={sortField}
                          sortOrder={sortOrder}
                          onSortChange={(field, order) => {
                            setSortField(field);
                            setSortOrder(order);
                            setPage(1); // restart pagination
                          }}
                          page={page}
                          pages={pages}
                          pageSize={pageSize}
                          pageSizes={pageSizes}
                          setPage={setPage}
                          setPageSize={setPageSize}
                          totalCount={totalCountBasedOnFilter}
                          navigate={navigate}
                          title={t("treatmentPlan.treatmentPlans")}
                          noItemsText={t("noItems.noTreatmentPlans")}
                          renderItem={(treatmentPlan) => (
                            <TreatmentPlanCardMobile
                              key={treatmentPlan._id}
                              treatmentPlan={treatmentPlan}
                              currencySymbol={currencySymbol}
                              onRowClick={(planId) => {
                                // console.log("onrowclick planId", planId);
                                setPlanId(planId);
                                openEditSidePanel();
                              }}
                              // onDelete={() => {
                              //   setUserId(patient._id);
                              //   setShowDeleteConfirmation(true);
                              //   setPatientName(
                              //     `${patient.lastName} ${patient.firstName}`
                              //   );
                              // }}
                            />
                          )}
                          selectOptions={
                            <>
                              <option value="patientID-desc">
                                {t("sort.recentFirst")}
                              </option>
                              <option value="patientID-asc">
                                {t("sort.oldestFirst")}
                              </option>
                              <option value="lastName-asc">
                                {t("sort.nameAZ")}
                              </option>
                              <option value="lastName-desc">
                                {t("sort.nameZA")}
                              </option>
                            </>
                          }
                        />
                      </>
                    ) : (
                      <Text color={textSecondary}>
                        {t("noItems.noTreatmentPlans")}
                      </Text>
                    )}
                  </Box>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p={["3rem 1.5rem", "3rem 2rem"]}
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
};

export default TreatmentPlanListNew;
